import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";

import company from "../data/company";
import { useInquiryModal } from "../context/InquiryModalContext";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  company: "",
  requirement: "",
  quantity: "",
  message: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full p-3 rounded bg-cream-50 border border-gold-500/50 text-maroon-900 placeholder:text-maroon-900/55 focus:outline-none focus:ring-2 focus:ring-gold-500 font-body text-sm";
const labelClass = "block font-accent font-medium text-maroon-900/85 text-xs tracking-wide uppercase mb-1.5";

function buildMailto(form) {
  const subject = `Manufacturing Inquiry from ${form.name || "Website Visitor"}`;
  const lines = [
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    form.phone ? `Phone: ${form.phone}` : null,
    form.company ? `Company/Brand: ${form.company}` : null,
    form.requirement ? `Garment/Product Requirement: ${form.requirement}` : null,
    form.quantity ? `Estimated Quantity: ${form.quantity}` : null,
    "",
    "Message:",
    form.message,
  ].filter((line) => line !== null);

  const body = lines.join("\n");
  return `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function InquiryModal() {
  const { isOpen, closeInquiry } = useInquiryModal();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [handedOff, setHandedOff] = useState(false);
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setHandedOff(false);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);

    function onKeyDown(e) {
      if (e.key === "Escape") closeInquiry();
    }
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeInquiry]);

  function updateField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Please enter your name.";
    if (!form.email.trim()) {
      nextErrors.email = "Please enter your email.";
    } else if (!EMAIL_RE.test(form.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!form.message.trim()) nextErrors.message = "Please tell us a bit about your requirement.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    window.location.href = buildMailto(form);
    setHandedOff(true);
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="absolute inset-0 bg-maroon-900/60"
            onClick={closeInquiry}
            aria-hidden="true"
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-modal-title"
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-cream-50 rounded-2xl border-2 border-gold-500/40 shadow-2xl p-6 sm:p-8"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.3 }}
          >
            <button
              type="button"
              onClick={closeInquiry}
              aria-label="Close inquiry form"
              className="absolute top-4 right-4 text-maroon-700 hover:text-gold-700 transition-colors p-1"
            >
              <FiX className="h-6 w-6" />
            </button>

            <h2 id="inquiry-modal-title" className="font-heading text-maroon-800 text-xl sm:text-2xl font-semibold mb-2">
              Send an Inquiry
            </h2>
            <p className="font-body text-maroon-900/90 text-sm leading-relaxed mb-6">
              Tell us about your requirement and we'll get back to you at{" "}
              <a href={`mailto:${company.email}`} className="text-maroon-700 font-semibold hover:underline">
                {company.email}
              </a>
              .
            </p>

            {handedOff ? (
              <div className="p-4 bg-gold-100/60 border border-gold-500 rounded-xl">
                <p className="font-body text-maroon-800 text-sm leading-relaxed">
                  Your email app should now be open with this inquiry pre-filled and addressed to {company.email}.
                  Please review it and hit send from there.
                </p>
                <button
                  type="button"
                  onClick={closeInquiry}
                  className="mt-4 px-5 py-2 bg-maroon-700 text-cream-100 font-accent text-xs tracking-wide rounded-full hover:bg-maroon-800 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div>
                  <label htmlFor="inquiry-name" className={labelClass}>
                    Name <span className="text-maroon-700">*</span>
                  </label>
                  <input
                    id="inquiry-name"
                    ref={firstFieldRef}
                    className={inputClass}
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "inquiry-name-error" : undefined}
                  />
                  {errors.name && (
                    <p id="inquiry-name-error" className="text-xs text-maroon-700 mt-1">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="inquiry-email" className={labelClass}>
                      Email <span className="text-maroon-700">*</span>
                    </label>
                    <input
                      id="inquiry-email"
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "inquiry-email-error" : undefined}
                    />
                    {errors.email && (
                      <p id="inquiry-email-error" className="text-xs text-maroon-700 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="inquiry-phone" className={labelClass}>
                      Phone
                    </label>
                    <input
                      id="inquiry-phone"
                      type="tel"
                      className={inputClass}
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="inquiry-company" className={labelClass}>
                    Company / Brand
                  </label>
                  <input
                    id="inquiry-company"
                    className={inputClass}
                    value={form.company}
                    onChange={(e) => updateField("company", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="inquiry-requirement" className={labelClass}>
                      Garment / Product Requirement
                    </label>
                    <input
                      id="inquiry-requirement"
                      className={inputClass}
                      value={form.requirement}
                      onChange={(e) => updateField("requirement", e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="inquiry-quantity" className={labelClass}>
                      Estimated Quantity
                    </label>
                    <input
                      id="inquiry-quantity"
                      className={inputClass}
                      value={form.quantity}
                      onChange={(e) => updateField("quantity", e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="inquiry-message" className={labelClass}>
                    Message <span className="text-maroon-700">*</span>
                  </label>
                  <textarea
                    id="inquiry-message"
                    rows="4"
                    className={inputClass}
                    value={form.message}
                    onChange={(e) => updateField("message", e.target.value)}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "inquiry-message-error" : undefined}
                  />
                  {errors.message && (
                    <p id="inquiry-message-error" className="text-xs text-maroon-700 mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-maroon-700 text-cream-100 font-accent tracking-wide text-xs rounded-full hover:bg-maroon-800 transition-colors"
                  >
                    Send Inquiry
                  </button>
                  <button
                    type="button"
                    onClick={closeInquiry}
                    className="px-6 py-2.5 border border-maroon-700/40 text-maroon-700 font-accent tracking-wide text-xs rounded-full hover:border-gold-500 hover:text-gold-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
